import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useCustomOrderForm, TIME_WINDOWS } from './useCustomOrderForm';
import { CustomOrderProducts, CustomOrderItems } from './CustomOrderItems';

const prettyDate = (d) => (d
  ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  : '');

/**
 * Booking a walk-in uses the regular New Order form's layout and interactions —
 * a browsable product list and the same item table — with room for a line the
 * catalog does not have. Shared by the admin and the staff portal.
 */
export const CustomOrderFormDialog = ({
  open,
  onOpenChange,
  order = null,
  products = [],
  loadingProducts = false,
  defaultDate = '',
  onSubmit,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const f = useCustomOrderForm({ open, order, defaultDate });
  const isEdit = !!order;

  const handleSubmit = async () => {
    const problem = f.validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const result = await onSubmit(f.buildPayload(), order?.id || null);
      const flagged = result?.batches_flagged || [];
      toast.success(isEdit
        ? `${order.order_number || 'Custom order'} saved`
        : `Custom order ${result?.order_number || ''} created`.trim());
      flagged.forEach((b) => toast.warning(
        `Batch for ${prettyDate(b.production_date)} flagged for re-sync — the kitchen picks the change up when they check for new orders.`,
        { duration: 7000 },
      ));
      onOpenChange(false);
      if (onSaved) await onSaved(result);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save this custom order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="custom-order-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${order.order_number || 'Custom Order'}` : 'New Custom Order'}
          </DialogTitle>
          <DialogDescription>
            A one-off order for a customer who isn&apos;t a portal client — walk-in, prospect or party tray.
          </DialogDescription>
        </DialogHeader>

        {f.blockedReason ? (
          <div className="space-y-4" data-testid="custom-order-edit-blocked">
            <p className="text-sm text-gray-700">{f.blockedReason}</p>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Customer + fulfilment — the client picker's slot */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={f.form.customer_name}
                  onChange={(e) => f.setField('customer_name', e.target.value)}
                  placeholder="e.g. Walk-in — John Smith"
                  data-testid="custom-order-customer-name"
                />
              </div>
              <div>
                <Label>Fulfillment</Label>
                <Select value={f.form.order_type} onValueChange={(v) => f.setField('order_type', v)}>
                  <SelectTrigger data-testid="custom-order-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="pickup">Pickup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date + window */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{f.form.order_type === 'pickup' ? 'Pickup Date *' : 'Delivery Date *'}</Label>
                <Input
                  type="date"
                  value={f.form.order_date}
                  onChange={(e) => f.setField('order_date', e.target.value)}
                  data-testid="custom-order-date"
                />
                {f.showBakingDay ? (
                  <div className="mt-2">
                    <Label className="text-xs text-gray-500">Baking day</Label>
                    <Input
                      type="date"
                      value={f.form.baking_date}
                      onChange={(e) => f.setField('baking_date', e.target.value)}
                      className="h-8 text-sm"
                      data-testid="custom-order-baking-day"
                    />
                    <button
                      type="button"
                      className="text-xs text-amber-700 underline mt-1"
                      onClick={() => { f.setShowBakingDay(false); f.setField('baking_date', ''); }}
                      data-testid="custom-order-baking-day-reset"
                    >
                      Bake on the same day
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:text-amber-700 mt-1"
                    onClick={() => f.setShowBakingDay(true)}
                    data-testid="custom-order-baking-day-toggle"
                  >
                    Baked on the same day · <span className="underline">bake it earlier</span>
                  </button>
                )}
              </div>
              {f.form.order_type === 'delivery' ? (
                <div>
                  <Label>Delivery Time</Label>
                  <Select
                    value={f.form.delivery_time_window}
                    onValueChange={(v) => f.setField('delivery_time_window', v)}
                  >
                    <SelectTrigger data-testid="custom-order-time-window">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_WINDOWS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Pickup Time</Label>
                  <Input
                    value={f.form.pickup_time}
                    onChange={(e) => f.setField('pickup_time', e.target.value)}
                    placeholder="e.g. 8:30 AM"
                    data-testid="custom-order-pickup-time"
                  />
                </div>
              )}
            </div>

            {/* Delivery address */}
            {f.form.order_type === 'delivery' && (
              <div className="space-y-3">
                <div>
                  <Label>Delivery Address</Label>
                  <Input
                    value={f.form.delivery_address}
                    onChange={(e) => f.setField('delivery_address', e.target.value)}
                    placeholder="Street address"
                    data-testid="custom-order-address"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={f.form.delivery_city}
                    onChange={(e) => f.setField('delivery_city', e.target.value)}
                    placeholder="City"
                    data-testid="custom-order-city"
                  />
                  <Input
                    value={f.form.delivery_state}
                    onChange={(e) => f.setField('delivery_state', e.target.value)}
                    placeholder="State"
                    data-testid="custom-order-state"
                  />
                  <Input
                    value={f.form.delivery_zip}
                    onChange={(e) => f.setField('delivery_zip', e.target.value)}
                    placeholder="ZIP"
                    data-testid="custom-order-zip"
                  />
                </div>
                <Textarea
                  value={f.form.delivery_instructions}
                  onChange={(e) => f.setField('delivery_instructions', e.target.value)}
                  placeholder="Delivery instructions (optional)"
                  rows={2}
                  data-testid="custom-order-instructions"
                />
              </div>
            )}

            <CustomOrderProducts
              products={products}
              items={f.items}
              loading={loadingProducts}
              onAdd={f.addProduct}
              onAddOneOff={f.addOneOff}
            />

            <CustomOrderItems
              items={f.items}
              products={products}
              total={f.total}
              onPatch={f.patchLine}
              onLink={f.linkToProduct}
              onRemove={f.removeItem}
            />

            <div>
              <Label>Notes</Label>
              <Textarea
                value={f.form.notes}
                onChange={(e) => f.setField('notes', e.target.value)}
                placeholder="Optional notes for the kitchen"
                rows={2}
                data-testid="custom-order-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 min-h-[44px]"
                onClick={handleSubmit}
                disabled={saving}
                data-testid="submit-custom-order-btn"
              >
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Custom Order'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomOrderFormDialog;
